import { useMemo } from 'react';
import { Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  loadablePlateCount,
  type PlateInventoryEntry,
  type PlateLoadMode,
  type PlatePairLoaded,
} from '@heraion/shared';

import { Chip } from '../primitives/Chip';
import type { useTheme } from './theme';
import {
  activeDenominations,
  formatUnitValue,
  formatUnitWeight,
  weightLocale,
} from './plateCalcMath';
import { unitWord, weightUnitLabels } from '../weightUnitLabels';
import type { makeStyles } from './plateCalcModalStyles';
import type { WeightUnit } from './state/loggerDraft';

// The plate calculator's COMPOSE row (LG-37b fetta B): tap a plate and the
// working value goes up.
//
// It is the direction the modal never had. Until now the only way in was to
// type a target and read the load-out back — which is the wrong way round when
// the bar in front of you is ALREADY loaded and the question is "what does
// this weigh?". Antenore, 10/09: «carico una macchina con vari piatti e voglio
// sapere quanto ho messo».
//
// ⚠️ A tap is worth `plateStep` — 2× the plate on two sides, 1× on one side.
// That rule is NOT written here: it lives once in plateCalculator.ts beside
// the greedy that also needs it, because a second hand-written ×2 is how the
// two drift the day the rule gains a case.
//
// ⭐ ONE ROW, and only the weights — Antenore's correction on the phone
// (10/09, second look). The first build gave every chip a sub-line that read
// "—" when that plate was not on the bar, and a "−" button of its own; eight
// denominations then wrapped onto three lines and pushed the answer down the
// screen. His words: «cosa sono quei trattini? deve starci tutto su una
// linea». So:
//   · a chip is a weight, nothing else. No count, no dash.
//   · taking a plate OFF happens in the summary, on the row that already
//     names that plate and its count — [[feedback_information_goes_where_it_already_has_a_home]].
//   · the row does not wrap: the chips share the width with `flex: 1`.
// ⛔ Which also means the count is no longer visible on the chip, so the
// screen-reader hint below is the only place it is spoken from here. It stays.

type Props = {
  theme: ReturnType<typeof useTheme>;
  styles: ReturnType<typeof makeStyles>;
  /** What the gym has — drives which denominations get a chip at all. An
   *  athlete who zeroes a size in the inventory below loses its chip, which is
   *  the intended way to shorten this row (Antenore: «può essere accettabile»). */
  inventory: ReadonlyArray<PlateInventoryEntry>;
  /** What is on the bar right now, whether composed or greedy-resolved. Used
   *  only to say when a size is exhausted and to speak its count. */
  loaded: ReadonlyArray<PlatePairLoaded>;
  mode: PlateLoadMode;
  /** The ROW's unit. The sizes below are face values in it — what is stamped
   *  on that gym's iron — so this says how to write them, never how to convert
   *  them (`equipment-model.md` «L'invariante che sopravvive a tutto»). */
  unit: WeightUnit;
  onAdd: (size: number) => void;
};

export function PlateCalcComposeSection({
  theme,
  styles,
  inventory,
  loaded,
  mode,
  unit,
  onAdd,
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = weightLocale(i18n);
  const unitLabels = weightUnitLabels(t);

  const denominations = useMemo(
    () => activeDenominations(inventory, unit),
    [inventory, unit],
  );

  const countBySize = useMemo(() => {
    const map = new Map<number, number>();
    for (const p of loaded) map.set(p.size, p.pairs);
    return map;
  }, [loaded]);

  return (
    <View style={styles.composeSection}>
      {/* ⭐ L'unità è detta UNA volta, qui nel titolo (LG-37b E2, sua
          decisione dell'11/09): i chip sotto restano nudi, come sono sempre
          stati, e ora lo sono anche quelli della barra e dell'inventario. */}
      <Text style={styles.sectionLabel}>
        {t('mobile.plates.composeTitle', { unit: unitWord(t, unit) })}
      </Text>
      {denominations.length === 0 ? (
        <Text style={styles.outputBody}>{t('mobile.plates.composeEmpty')}</Text>
      ) : (
        <View style={styles.composeRow}>
          {denominations.map((size) => {
            const count = countBySize.get(size) ?? 0;
            // ⛔ Bare, no suffix: the unit is already said once beside the
            // weight field, and repeating it on eight chips is what wrapped
            // this row onto three lines the first time.
            const sizeLabel = formatUnitValue(size, unit, locale);
            const single = mode === 'single';
            return (
              <Chip
                key={size}
                theme={theme}
                shape="box"
                style={styles.composeChip}
                label={sizeLabel}
                // ⛔ No hitSlop: the box is a real 44 instead. Seven or eight
                // controls share one row, so an invisible skirt on each would
                // put every zone on top of its neighbour's.
                hitSlop={0}
                // At the inventory's limit the tap would be a no-op: the
                // composition clamps, so the value would not move. Saying so
                // is better than a chip that quietly does nothing.
                disabled={count >= loadablePlateCount(inventory, size, mode)}
                // The WEIGHT — number and unit together — is interpolated INTO
                // the sentence, never appended to it: German ends on the verb
                // ("Ein Paar 25 kg hinzufügen") and no concatenation can
                // produce that. Spoken with its unit even though the chip is
                // bare: a screen reader reads this row out of context.
                accessibilityLabel={t(
                  single
                    ? 'mobile.plates.composeAddPlate'
                    : 'mobile.plates.composeAddPair',
                  { weight: formatUnitWeight(size, unit, locale, unitLabels) },
                )}
                // The chip no longer shows how many are on: without this the
                // count would be visible nowhere on this row for a screen
                // reader, and the summary is a separate stop in the traversal.
                accessibilityHint={
                  count > 0
                    ? t(
                        single ? 'mobile.plates.plates' : 'mobile.plates.pairs',
                        { count },
                      )
                    : undefined
                }
                onPress={() => onAdd(size)}
              />
            );
          })}
        </View>
      )}
    </View>
  );
}
